import { z } from 'zod';
import { imageCropSchema } from './component-base.schemas';

// Blog App schemas
export const blogLayoutSchema = z.enum(['grid', 'list', 'magazine']);
export const blogPostStatusSchema = z.enum(['draft', 'published']);

export const blogSchema = z.object({
  id: z.string().uuid(),
  siteId: z.string().uuid(),
  basePath: z.string().default('/blog'),
  title: z.string().default('Blog'),
  description: z.string().optional(),
  postsPerPage: z.number().int().min(1).max(50).default(10),
  layout: blogLayoutSchema.default('grid'),
  showAuthor: z.boolean().default(true),
  showDate: z.boolean().default(true),
  showReadingTime: z.boolean().default(true),
  showFeaturedImage: z.boolean().default(true),
  showTags: z.boolean().default(true),
  isPublished: z.boolean().default(false),
  layoutId: z.string().uuid().nullable().optional(),
  showLayout: z.boolean().default(true),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const blogPostSchema = z.object({
  id: z.string().uuid(),
  blogId: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  content: z.string().optional(),
  excerpt: z.string().optional(),
  featuredImageUrl: z.string().optional(),
  featuredImageCrop: imageCropSchema,
  authorName: z.string().optional(),
  status: blogPostStatusSchema.default('draft'),
  publishedAt: z.string().datetime().optional(),
  seoTitle: z.string().optional(),
  seoDescription: z.string().optional(),
  seoImageUrl: z.string().optional(),
  readingTimeMinutes: z.number().int().optional(),
  viewCount: z.number().int().default(0),
  // Draft → Published snapshot fields
  draftContentJson: z.string().nullable().optional(),
  publishedContentJson: z.string().nullable().optional(),
  publishedHtml: z.string().nullable().optional(),
  publishedHash: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  tags: z.array(z.string()).optional(),
});

export const createBlogSchema = z.object({
  basePath: z.string().default('/blog'),
  title: z.string().default('Blog'),
  description: z.string().optional(),
  postsPerPage: z.number().int().min(1).max(50).default(10),
  layout: blogLayoutSchema.default('grid'),
  showAuthor: z.boolean().default(true),
  showDate: z.boolean().default(true),
  showReadingTime: z.boolean().default(true),
  showFeaturedImage: z.boolean().default(true),
  showTags: z.boolean().default(true),
});

// Blog custom colors schema for color overrides
export const blogCustomColorsSchema = z
  .object({
    // Background colors
    pageBackground: z.string().optional(),
    sidebarBackground: z.string().optional(),
    cardBackground: z.string().optional(),
    // Text colors
    headingColor: z.string().optional(),
    textColor: z.string().optional(),
    mutedTextColor: z.string().optional(),
    // Post colors
    postTitleColor: z.string().optional(),
    postTextColor: z.string().optional(),
    postMetaColor: z.string().optional(),
    // Link colors
    linkColor: z.string().optional(),
    linkHoverColor: z.string().optional(),
    // Accent colors
    primaryColor: z.string().optional(),
    tagBackground: z.string().optional(),
    tagText: z.string().optional(),
    // Border colors
    borderColor: z.string().optional(),
    cardBorderColor: z.string().optional(),
  })
  .optional()
  .nullable();

export const updateBlogSchema = z.object({
  basePath: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  postsPerPage: z.number().int().min(1).max(50).optional(),
  layout: blogLayoutSchema.optional(),
  showAuthor: z.boolean().optional(),
  showDate: z.boolean().optional(),
  showReadingTime: z.boolean().optional(),
  showFeaturedImage: z.boolean().optional(),
  showTags: z.boolean().optional(),
  layoutId: z.string().uuid().nullable().optional(),
  showLayout: z.boolean().optional(),
  customColors: blogCustomColorsSchema,
});

export const createBlogPostSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z.string().optional(), // Optional custom slug, will auto-generate from title if not provided
  content: z.string().optional(),
  excerpt: z.string().optional(),
  featuredImageUrl: z.string().optional(),
  featuredImageCrop: imageCropSchema,
  authorName: z.string().optional(),
  status: blogPostStatusSchema.default('draft'),
  seoTitle: z.string().optional(),
  seoDescription: z.string().optional(),
  seoImageUrl: z.string().optional(),
  tags: z.array(z.string()).optional(),
});

export const updateBlogPostSchema = z.object({
  title: z.string().optional(),
  slug: z.string().optional(), // Optional custom slug, will be validated for uniqueness
  content: z.string().optional(),
  excerpt: z.string().optional(),
  featuredImageUrl: z.string().optional(),
  featuredImageCrop: imageCropSchema,
  authorName: z.string().optional(),
  status: blogPostStatusSchema.optional(),
  seoTitle: z.string().nullable().optional(),
  seoDescription: z.string().nullable().optional(),
  seoImageUrl: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
});

export const blogStatsSchema = z.object({
  totalPosts: z.number().int(),
  publishedPosts: z.number().int(),
  draftPosts: z.number().int(),
  totalViews: z.number().int(),
  totalTags: z.number().int(),
});

// Blog App type exports
export type BlogLayout = z.infer<typeof blogLayoutSchema>;
export type BlogPostStatus = z.infer<typeof blogPostStatusSchema>;
export type Blog = z.infer<typeof blogSchema>;
export type BlogPost = z.infer<typeof blogPostSchema>;
export type CreateBlogRequest = z.infer<typeof createBlogSchema>;
export type UpdateBlogRequest = z.infer<typeof updateBlogSchema>;
export type CreateBlogPostRequest = z.infer<typeof createBlogPostSchema>;
export type UpdateBlogPostRequest = z.infer<typeof updateBlogPostSchema>;
export type BlogStats = z.infer<typeof blogStatsSchema>;
